import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import CommentSection from '../CommentSection';
import ModalWindow from '../modalWindow';
import Spinner from '../spinner/Spinner';
import {
    fetchProductById,
    fetchProductDetails,
    recordProductView,

} from '../../services/services';
import ImageForProductById from '../imageForProductById';
import InfoForProductById from '../infoForProductById';
import ProductRecommendations from '../productRecommendations';
import { clearCurProductState, setCurProductMaterialPreview, setCurProductSelectedMaterial } from '../../redux/slices/curProductSlice';
import { MaterialPriceAndImages } from '../interfaces';
import CatalogEmptyState from '../catalogEmptyState/CatalogEmptyState';
import './productById.scss';

type ProductRouteState = {
  selectedMaterial?: string;
  materialPreview?: MaterialPriceAndImages;
};

const ProductById: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const routeState = location.state as ProductRouteState | null;
    const dispatch = useAppDispatch();
    const status = useAppSelector((state) => state.curProduct.status);
    const curProduct = useAppSelector((state) => state.curProduct.productId);
    const userId = useAppSelector((state) => state.user.user?.id);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const recordedProductIdRef = useRef<string | null>(null);
    const hasRouteProductMismatch = Boolean(id && curProduct && curProduct.id !== id);
  
    useEffect(() => {
        if (id) {
          dispatch(fetchProductById(id)).then(() => {
            if (routeState?.materialPreview) {
              dispatch(setCurProductMaterialPreview(routeState.materialPreview));
            } else if (routeState?.selectedMaterial) {
              dispatch(setCurProductSelectedMaterial(routeState.selectedMaterial));
            }
          });
          dispatch(fetchProductDetails({ id }));
        }
    
        return () => {
          dispatch(clearCurProductState());
        };
      }, [dispatch, id, routeState?.materialPreview, routeState?.selectedMaterial]);

    useEffect(() => {
      if (!userId || status !== 'succeeded' || !curProduct?.id || recordedProductIdRef.current === curProduct.id) {
        return;
      }

      recordedProductIdRef.current = curProduct.id;
      void dispatch(recordProductView(curProduct.id));
    }, [curProduct?.id, dispatch, status, userId]);
  

  
    if (status === 'loading' || status === 'idle' || hasRouteProductMismatch) {
      return (
        <div className="product-block product-block--loading">
          <Spinner />
        </div>
      );
    }
  
    if ((status === "succeeded" && !curProduct) || status === "failed") {
      return (
        <div className="product-block">
          <CatalogEmptyState
            eyebrow="Product unavailable"
            title="This piece is not available."
            message="It may have been removed, sold out, or moved to another collection. Keep browsing the catalog to find a similar piece."
            primaryAction={{ label: 'View all jewelry', to: '/products' }}
            secondaryAction={{ label: 'Shop rings', to: '/products/types/Rings' }}
          />
        </div>
      );
    }
  
    return (
      <div className="product-block">
        <div className="product-content">
          <ImageForProductById />
          <InfoForProductById
          />
          <ModalWindow visible={isModalVisible} onCancel={() => setIsModalVisible(false)} />
        </div>
        <ProductRecommendations productId={curProduct?.id} />
        <CommentSection />
      </div>
    );
  };
  
  export default ProductById;
